import { Component, OnInit, Input, Output, EventEmitter, DOCUMENT, inject, HostListener, OnDestroy, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { VarianceService } from 'src/app/services/variance.service';
import { getLocalTimeOffset } from 'src/app/shared/timezone.util';
import { toStartEndFilterDateRange } from 'src/app/shared/date-filter.util';

import { MessageService, SelectItem } from 'primeng/api';
import { AuthService } from 'src/app/services/auth.service';
import { environment } from 'src/environments/environment';
import { ActivatedRoute } from "@angular/router";
import { EMPTY, Subject } from 'rxjs';
import { catchError, debounceTime, map, switchMap, takeUntil } from 'rxjs/operators';
import { LocationAccessService } from 'src/app/services/location-access.service';
import { ILogger, LOGGER_TOKEN } from 'src/app/services/logger/ilogger';
import { Table } from 'primeng/table';
import { cloneFilterState, DataFilterService, FilterState } from 'src/app/services/data-filter.service';
import { PrintAuthUrlService } from 'src/app/services/print-auth-url.service';

@Component({
    selector: 'app-ehr',
    templateUrl: './ehr.component.html',
    styleUrls: ['./ehr.component.css'],
    standalone: false
})
export class EhrComponent implements OnInit, OnDestroy, OnChanges {
  private readonly logger = inject<ILogger>(LOGGER_TOKEN);
  private document = inject<Document>(DOCUMENT);
  private varService = inject(VarianceService);
  private messageService = inject(MessageService);
  private route = inject(ActivatedRoute);
  private locationAccessService = inject(LocationAccessService);
  private dataFilterService = inject(DataFilterService);
  private printAuthUrlService = inject(PrintAuthUrlService);
  authService = inject(AuthService);

  cols:any[];
  records:any[] = [];
  totalCount:any = 0;
  filter:any = {
    duration: 7,
    ...this.locationAccessService.buildAuthorizedFilters(
      this.locationAccessService.buildInitialFilters(this.authService.user),
      this.authService.user
    ),
    serviceGroup: "All",
    deIdentifyData: true,
    _id: [],
  };
  filterDraft: any = {};
  private filterState?: FilterState<any>;
  @ViewChild('dataTable') dataTable?: Table;
  screenWidth: number = window.innerWidth; // Track screen width
  filterDialog   = false;
  filterOptions:any={};
  dataLoaded = true;
  tableDataIsLoading = false;
  initialized = false;
  locationInitialized = false; // Track if location select has initialized
  isPrintEhr = false;
  skip_record = 0;
  masterSelected = false;
  selectedCount = 0;
  // Ids the user has manually deselected while `masterSelected` (Select-All-across-pages)
  // is active. Kept as a Set so add/delete/has stay O(1) as the user paginates.
  excludedIds = new Set<string>();
  private dataStale = false;
  private filterInProgress = false;
  private loadSequence = 0;
  selectedIds: any[] = [];
  footprint: string = environment.footprint;
  searchDialog = false;
  locationSelectFilter: any = {
    region: [],
    campus: [],
    facility: [],
    locationGroup: [],
    footprint: environment.footprint,
    changeValue: true
  };
  private readonly destroy$ = new Subject<void>();
  private readonly autocompleteSearch$ = new Subject<{ event: { term: string; items: any[] }; key: string; field: string }>();

  @Input() lookbackPeriod!:number;
  @Input() quickTimeOptions: SelectItem[] = [];
  @Input() deId=false;
  @Input() active = true;
  @Output() lookbackPeriodChange = new EventEmitter<number>();
  @Output() filterValue = new EventEmitter();
  @Output() dateValue = new EventEmitter();

  constructor() {
    this.cols = [
      { field: 'PatientName', header: 'Patient Name' },
      { field: 'PatientID', header: 'Patient ID' },
      { field: 'MedDescription', header: 'Med Description' },
      { field: 'Device', header: 'Stock Area' },
      { field: 'UserName', header: 'User Name' },
      { field: 'UserID', header: 'User ID' },
      { field: 'UserType', header: 'Role' },
      { field: 'Discrepancy', header: 'Var/Qty' },
      { field: 'UOM', header: 'UOM' },
      { field: "varDateString", header: "Time of Administration" },
      { field: 'TransactionType', header: 'Transaction Type' },
    ];
  }

  ngOnInit(): void {
    this.route.data.pipe(takeUntil(this.destroy$)).subscribe(x=>this.deId = x.deId)

    this.autocompleteSearch$.pipe(
      debounceTime(300),
      switchMap(({ event, key, field }) =>
        this.varService.searchAutoComplete(field, event.term, this.filter).pipe(
          map(res => ({ res: res as any[], key, event })),
          catchError(() => EMPTY)
        )
      ),
      takeUntil(this.destroy$)
    ).subscribe(({ res, key, event }) => {
      if (key === 'adminId' && this.deId) {
        const searchTerm = event.term.toLowerCase();
        const filteredOptions = this.filterOptions[key].filter((option: any) => {
          if (option.label === 'All') return true;
          return option.label.toLowerCase().includes(searchTerm);
        });
        if (filteredOptions.length === 1 && res.length > 0) {
          const newDeIdentifiedOptions = res.sort().map((item: any, index: number) => ({
            label: "AdminID" + (index + 1),
            value: item
          }));
          this.filterOptions[key] = [{ label: "All", value: "All" }].concat(newDeIdentifiedOptions);
        } else {
          this.filterOptions[key] = filteredOptions;
        }
      } else if (key === 'patientId' && this.deId) {
        const searchTerm = event.term.toLowerCase();
        const filteredOptions = this.filterOptions[key].filter((option: any) => {
          if (option.label === 'All') return true;
          return option.label.toLowerCase().includes(searchTerm);
        });
        if (filteredOptions.length === 1 && res.length > 0) {
          const newDeIdentifiedOptions = res.sort().map((item: any, index: number) => ({
            label: "patientId" + (index + 1),
            value: item
          }));
          this.filterOptions[key] = [{ label: "All", value: "All" }].concat(newDeIdentifiedOptions);
        } else {
          this.filterOptions[key] = filteredOptions;
        }
      } else {
        this.filterOptions[key] = ["All"].concat(res);
      }
    });

    this.locationSelectFilter = this.locationAccessService.buildInitialFilters(this.authService.user, {
      footprint: this.footprint,
      changeValue: true
    });
    // Do not call initComponent() here; wait for location select to initialize
  }

  // Handler for location select initialization
  onLocationSelectInitialized(initialFilter: any): void {
    if (this.locationInitialized) return;
    this.locationAccessService.applyAuthorizedFilters(this.filter, initialFilter, this.authService.user);
    this.captureInitialFilter();
    this.locationInitialized = true;
    this.initComponent();
  }

  initComponent(){
    if(this.initialized)
    return
    if(!this.locationInitialized || !this.active) return;
    this.initialized = true;
    // this.setFilterOptions();
    this.filterRecords()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['active'] && changes['active'].currentValue && !changes['active'].firstChange) {
      if (this.dataStale && this.initialized) {
        this.dataStale = false;
        this.filterRecords();
      } else {
        this.initComponent();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('window:resize')
  updateScreenWidth(): void {
    this.screenWidth = window.innerWidth; // Update screen width on resize
  }

  updateLookbackPeriod(lookbackPeriod: any) {
    this.filter.duration = lookbackPeriod;
    if (this.active && this.initialized) {
      this.filterRecords();
    } else {
      this.dataStale = true;
    }
  }

  onLocationChanged(event: any){
    this.filterDialog = false;
    this.locationAccessService.applyAuthorizedFilters(this.filter, event, this.authService.user);
    this.filterRecords();
  }

  onServiceGrpChanged(event: any){
    this.filter.serviceGroup = event.serviceGroup;
    this.filterRecords();
  }

  setFilterOptions(){
    this.dataLoaded = false;
    this.varService.fetchEhrAdcFilterOptions(this.filter).subscribe(res=>{
      this.filterDialog = !this.filterDialog;
      this.filterOptions = res;
      if(this.deId){
        this.filterOptions.adminId = [{ label: "All", value: "All" }];
        this.varService.searchAutoComplete('adminid', '', this.filter).subscribe((res: any)=>{
          const adminIDdeId = res.map((item: any, index: number) => ({
            label: "AdminID" + (index + 1),
            value: item
          }));
          this.filterOptions.adminId = this.filterOptions.adminId.concat(adminIDdeId);
          });
      }else{
        this.filterOptions.adminId = [{ label: "All", value: "All" }];
        this.varService.searchAutoComplete('adminid', '', this.filter).subscribe((res: any)=>{
          this.filterOptions.adminId = this.filterOptions.adminId.concat(res);});
      }
      if(this.deId){
        this.filterOptions.patientId = [{ label: "All", value: "All" }];
        this.varService.searchAutoComplete('patientid', "", this.filter).subscribe((res: any)=>{
          const deIdentifiedRes = res.map((item: any, index: number) => ({
            label: "patientId" + (index + 1),
            value: item
          }));
          this.filterOptions.patientId = this.filterOptions.patientId.concat(deIdentifiedRes);
          });
      }else{
        this.filterOptions.patientId = [{ label: "All", value: "All" }];
        this.varService.searchAutoComplete('patientid', '', this.filter).subscribe((res: any)=>{
          this.filterOptions.patientId = this.filterOptions.patientId.concat(res);});
      }
      this.filterOptions.adminType = [{label:"All",value:"All"}].concat(this.filterOptions.adminType.sort().map(function(item: any){ return {label:item, value:item}}));
      this.filterOptions.loc = [{label:"All",value:"All"}].concat(this.filterOptions.loc.sort().map(function(item: any){ return {label:item, value:item}}));
      this.filterOptions.medDescription = [{label:"All",value:"All"}].concat(this.filterOptions.medDescription.sort().map(function(item: any){ return {label:item, value:item}}));
      this.dataLoaded = true;
    })
  }

  openFilterModal()
  {
    this.ensureFilterState();
    this.filterState = this.dataFilterService.submit(this.filterState!, this.filter);
    this.filterState = this.dataFilterService.open(this.filterState);
    this.filterDraft = cloneFilterState(this.filterState.draft);
    if(this.dataLoaded)
      this.setFilterOptions();
    else
      this.filterDialog = !this.filterDialog;
  }

  closeFilterModal()
  {
    this.ensureFilterState();
    this.filterState = this.dataFilterService.cancel(this.filterState!);
    this.filterDraft = cloneFilterState(this.filterState.draft);
    this.filterDialog = false;
  }

  submitAdvancedFilter(): void {
    this.ensureFilterState();
    this.filterState = this.dataFilterService.submit(this.filterState!, this.filterDraft);
    this.filter = cloneFilterState(this.filterState.applied);
    this.submitFilter();
  }

  submitFilter()
  {
    this.filterDialog = false;
    this.filterRecords()
  }

  onShowModal(){
    this.document.body.classList.add('ovrhidden');
  }

  onHideModal(){
    this.document.body.classList.remove('ovrhidden');
  }

  clearFilter()
  {
    this.ensureFilterState();
    this.filterState = this.dataFilterService.clear(this.filterState!, [
      'duration', 'regionId', 'campusId', 'facilityId', 'locationGroupingIds',
      'serviceGroup', 'deIdentifyData'
    ]);
    this.filter = cloneFilterState(this.filterState.applied);
    this.filterDraft = cloneFilterState(this.filterState.draft);
    this.filterRecords()
  }

  submitGlobalFilter(){
    this.filterRecords()
  }

  async filterRecords(){
    if (this.filterInProgress) return;
    this.filterInProgress = true;
    this.tableDataIsLoading = true;
    const obj = { first: 0 };
    // Any filter change invalidates the current global selection.
    this.clearSelection();
    this.filterValue.emit(cloneFilterState(this.filter));

    /**
     * MV2-350, resolve cross-ref filters before loading.
     * Convert UserName → UserID and PatientName → PatientID
     * so the backend receives the correct identifiers.
     */
    try {
      if (this.filter.UserName && this.filter.UserName !== "All") {
        await this.resolveAdminUID();
      }
      if (this.filter.PatientName && this.filter.PatientName !== "All") {
        await this.resolvePatientUID();
      }
    } catch (err) {
      this.logger.exception(err);
    }

    this.filterInProgress = false;
    this.loadRecordsLazy(obj);
  }

  private async resolveAdminUID() {
    const response = await this.varService.getNormalizedAdcEhrPagedData(0, {'UserName':this.filter.UserName}, 1).toPromise();
    if (response) {
      const adminUID = (response as any)["docs"][0]["UserID"];
      this.filter.UserID = adminUID;
    }
  }

  /**
   * MV2-350, resolve PatientName to PatientID before loading records.
   */
  private async resolvePatientUID() {
    const response = await this.varService.getNormalizedAdcEhrPagedData(0, {'PatientName':this.filter.PatientName}, 1).toPromise();
    if (response) {
      const patientUID = (response as any)["docs"][0]["PatientID"];
      this.filter.PatientID = patientUID;
    }
  }

  padLeft(text:string, padChar:string, size:number): string {
    return (String(padChar).repeat(size) + text).substr( (size * -1), size) ;
  }

  formatToDateString(date: Date)
  {
    const dateString = [this.padLeft((date.getMonth()+1).toString(), '0', 2),
                      this.padLeft((date.getDate()).toString(), '0', 2),
                      date.getFullYear()].join('/') + ' | ' +
                     [this.padLeft((date.getHours()).toString(), '0', 2),
                      this.padLeft((date.getMinutes()).toString(), '0', 2),].join(':') 
    return dateString;
  }

  loadRecordsLazy(event: any){
    if(!this.initialized)
    return;
    const loadId = ++this.loadSequence;
    if(event.sortField)
    {
      this.filter.$sortField = event.sortField
      this.filter.$sortOrder = event.sortOrder
    }
    this.tableDataIsLoading = true;
    const filter = Object.assign({},this.filter)
    if(!filter.PatientID)
    filter.PatientID = "All"
    if(!filter.PatientName)
    filter.PatientName = "All"
    if(!filter.UserID)
    filter.UserID = "All"
    if(!filter.UserName)
    filter.UserName = "All"
    if(this.filter.MedDescription)
    filter.MedDescription = this.filter.MedDescription.map((item: any)=>btoa(item))
    this.filterDialog = false;
    this.skip_record = event['first'];
    this.varService.getEhrPagedData(event["first"], filter).subscribe({
      next: (res) => {
        if (loadId !== this.loadSequence) return;
        this.dateValue.emit((res as any)['date']);
        this.totalCount = (res as any)["count"];
        this.records = (res as any)["docs"];
        this.records.forEach(record => {
          if (record["varDateString"])
          {
            const dateObject = new Date(record["varDateString"]);
            record["varDateString"] = this.formatToDateString(dateObject);
          }
        });
        this.setInitialSelectionState();
        this.tableDataIsLoading = false;

        if ((res as any)["count"] == 0)
        {
          this.messageService.add({
            severity: 'info',
            summary: 'No Results Found.',
            detail: 'Please select different filter criteria.'
          });
        }
      },
      error: (err) => {
        if (loadId !== this.loadSequence) return;
        this.logger.exception(err);
        this.tableDataIsLoading = false;
      }
    });
  }

  searchWithAutoComplete(event: any, key: any, field: any){
    this.autocompleteSearch$.next({ event, key, field });
  }
  ngSelectSearch(searchTerm: string, eachObject: any) {
    const invalid = /[[\]°"§%() {}=\\?´`'#<>|,;.:+_-\s]+/g;
    const replacedKey = searchTerm.replace(invalid, '')
    const newRegEx = new RegExp(replacedKey, 'gi');
    try {
      const purgedName = eachObject.value.replace(invalid, '')
      if (newRegEx.test(purgedName)) {
        return true
      }
      return false
    }
    catch {
      return false
    }
  }
  resetCode(key: any) {
    delete this.filter[key];
  }
  resetDraftCode(key: any): void {
    delete this.filterDraft[key];
  }

  removeVarianceFilters(filter: any){
    this.ensureFilterState();
    this.filterState = this.dataFilterService.submit(this.filterState!, cloneFilterState(filter || {}));
    this.filter = cloneFilterState(this.filterState.applied);
    this.submitFilter();
  }
  
  openVarianceFilter(filter: any){
    this.ensureFilterState();
    this.filterState = this.dataFilterService.submit(this.filterState!, cloneFilterState(filter || {}));
    this.filter = cloneFilterState(this.filterState.applied);
    this.openFilterModal();
  }

  refreshToInitialState(): void {
    this.ensureFilterState();
    this.filterState = this.dataFilterService.refresh(this.filterState!);
    this.filter = cloneFilterState(this.filterState.applied);
    this.filterDraft = cloneFilterState(this.filterState.draft);
    this.locationSelectFilter = this.locationAccessService.buildInitialFilters(this.authService.user, {
      footprint: this.footprint, changeValue: true
    });
    this.filterDialog = false;
    this.searchDialog = false;
    this.dataTable?.reset();
    this.filterValue.emit(cloneFilterState(this.filter));
    this.filterRecords();
  }

  private captureInitialFilter(): void {
    this.filterState = this.dataFilterService.create(this.filter);
    this.filter = cloneFilterState(this.filterState.applied);
    this.filterDraft = cloneFilterState(this.filterState.draft);
  }

  private ensureFilterState(): void {
    if (!this.filterState) this.captureInitialFilter();
  }

  // printEhr(){
  //   this.isPrintEhr = true;
  // }

  submitPrintFilter() {
    // The adcEhrPrinting endpoint already understands every filter param the
    // page fetch uses, so Select-All-across-pages sends the filter itself
    // (plus any manually deselected ids) instead of stuffing a long id list
    // into the URL. Individual selection keeps the existing _id=... path.
    const ehrFilter: any = toStartEndFilterDateRange({ ...this.filter });
    ehrFilter['RecordType'] = 'EHR';

    if (this.masterSelected) {
      delete ehrFilter._id;
      ehrFilter['selectAll'] = true;
      if (this.excludedIds.size > 0) {
        ehrFilter['excludedIds'] = Array.from(this.excludedIds).join(',');
      }
    } else {
      if (!this.selectedIds || this.selectedIds.length === 0) {
        alert('You must first select a transaction before printing.');
        return;
      }
      ehrFilter['_id'] = this.selectedIds;
    }

    const url = environment.apiUrl + 'adcEhrPrinting?$skip=0&$limit=2000&';
    const filterString = this.generateQueryStringFromObject(ehrFilter);
    const timeOffset = getLocalTimeOffset();
    this.printAuthUrlService.openAuthenticatedPrint(
      url + filterString + '&timeOffset=' + timeOffset,
      'location=yes,height=1847,width=1002'
    );
    this.clearSelection();
  }

  generateQueryStringFromObject(params: any){
    this.tryClearWhitespaceFromGlobalSearch(params);
    return Object.keys(params).map(key => key + '=' + params[key]).join('&');
  }

  tryClearWhitespaceFromGlobalSearch(params: any)
  {
    if(params.globalSearch)
    {
      const textToTrim: string = params.globalSearch;
      params.globalSearch = textToTrim.trim();
    }
  }

  printEhr() {
    if (!this.transactionSelections()) {
      alert('You must first select a transaction before printing.');
      return false;  
      }
    else{
      this.isPrintEhr = true;
    }
  }
	
  transactionSelections() {
      return this.getSelectedCount() > 0;
    }

  /**
   * Effective count for the print guard and the UI counter. In select-all mode
   * this is totalCount minus the user's individual exclusions; otherwise it is
   * the length of the explicitly selected id list.
   */
  private getSelectedCount(): number {
    if (this.masterSelected) {
      return Math.max(0, (this.totalCount || 0) - this.excludedIds.size);
    }
    return this.selectedIds.length;
  }

  isPrint(checked: boolean, rowData: any){
    const selectedId = rowData?._id;
    if (selectedId == null) return;
    rowData.isSelected = checked;

    if (checked) {
      if (this.masterSelected) {
        this.excludedIds.delete(String(selectedId));
      } else if (this.selectedIds.indexOf(selectedId) === -1) {
        this.selectedIds.push(selectedId);
      }
    } else {
      if (this.masterSelected) {
        this.excludedIds.add(String(selectedId));
      } else {
        const index: number = this.selectedIds.indexOf(selectedId);
        if (index !== -1) {
          this.selectedIds.splice(index, 1);
        }
      }
    }
    this.selectedCount = this.getSelectedCount();
  }

  checkUncheckAll(e: { checked: boolean } | any) {
    // PrimeNG's p-checkbox emits (onChange) with a { checked: boolean } payload
    // after [(ngModel)] has been updated. That is far more reliable than
    // reading e.target.checked from a raw DOM click, which is undefined when
    // the click lands on the box div/icon rather than the hidden native input.
    const checked = !!(e && e.checked);
    if (checked) {
      this.masterSelected = true;
      this.excludedIds.clear();
      this.selectedIds = [];
      for (const record of this.records) {
        record.isSelected = true;
      }
    } else {
      this.masterSelected = false;
      this.excludedIds.clear();
      this.selectedIds = [];
      for (const record of this.records) {
        record.isSelected = false;
      }
    }
    this.selectedCount = this.getSelectedCount();
  }

  deSelectChecks(){
    this.checkUncheckAll({ checked: false })
  }

  /**
   * Reset every piece of selection state. Called on filter changes and after
   * print submission because the current ids may no longer belong to the
   * result set the user is looking at.
   */
  private clearSelection(): void {
    this.masterSelected = false;
    this.excludedIds.clear();
    this.selectedIds = [];
    this.selectedCount = 0;
    if (this.filter) {
      this.filter._id = [];
    }
    for (const record of this.records) {
      if (record) record.isSelected = false;
    }
  }

  setInitialSelectionState(){
    if (this.masterSelected) {
      for (const record of this.records) {
        const id = record?.["_id"];
        if (id == null) continue;
        record.isSelected = !this.excludedIds.has(String(id));
      }
    } else {
      for (const record of this.records) {
        const id = record?.["_id"];
        if (id == null) continue;
        record.isSelected = this.selectedIds.indexOf(id) !== -1;
      }
    }
    this.selectedCount = this.getSelectedCount();
  }
  clearTableSearch(){
    this.filter.globalSearch = '';
  }

  updateEndDate(){
    this.filter.endDate = this.filter.startDate
  }
  isDeId(name: any){
    if(this.deId)
      return "xxxxxx xxxxxx"
    else
      return name
  }

  showSearchDialog(){
    this.searchDialog = true;
  }

  formatName(name: string): string {
    if (!name) return name;
    return name.replace(/,\s*/g, ', ');
  }
}
